/**
 * Work Orders Service - Unified service for both web and mobile platforms
 * Handles work order operations, notes, files, and services
 *
 * The method implementations live in ./workOrders/* modules (crud, search,
 * lifecycle, credits, services, notes, tags, files, invoicing, parsers) as
 * functions bound to the service instance; this file keeps the class shell,
 * instance state, and the public export surface.
 */
import { evaluateGuard } from './guard';
import * as parsers from './workOrders/parsers';
import * as crud from './workOrders/crud';
import * as search from './workOrders/search';
import * as lifecycle from './workOrders/lifecycle';
import * as credits from './workOrders/credits';
import * as adjustments from './workOrders/adjustments';
import * as services from './workOrders/services';
import * as notes from './workOrders/notes';
import * as tags from './workOrders/tags';
import * as files from './workOrders/files';
import * as invoicing from './workOrders/invoicing';
export { parseReopenError } from './workOrders/parsers';
export class WorkOrdersService {
    constructor(client, baseUrl, options = {}) {
        // Request deduplication cache: prevents duplicate concurrent API calls
        // (e.g. React StrictMode double-invocation on web).
        this._requestCache = new Map();
        // Parsers / normalizers
        this.normalizeVehicle = parsers.normalizeVehicle;
        this.normalizeTag = parsers.normalizeTag;
        this.normalizeTags = parsers.normalizeTags;
        this.normalizeWorkOrder = parsers.normalizeWorkOrder;
        this.buildListResponse = parsers.buildListResponse;
        // CRUD & core reads
        this.getAccountWorkOrders = crud.getAccountWorkOrders;
        this.getLocationWorkOrders = crud.getLocationWorkOrders;
        this.getWorkOrder = crud.getWorkOrder;
        this.createWorkOrder = crud.createWorkOrder;
        this.updateWorkOrder = crud.updateWorkOrder;
        this.updateWorkOrderVehicle = crud.updateWorkOrderVehicle;
        this.setThumbnail = crud.setThumbnail;
        this.clearThumbnail = crud.clearThumbnail;
        this.exportWorkOrders = crud.exportWorkOrders;
        this.getActivitiesByResourceId = crud.getActivitiesByResourceId;
        this.getPricingPermissions = crud.getPricingPermissions;
        this.getAvailableWorkOrderTypes = crud.getAvailableWorkOrderTypes;
        // Search
        this.searchWorkOrders = search.searchWorkOrders;
        this.searchAllWorkOrders = search.searchAllWorkOrders;
        this.clientSideSearchWorkOrders = search.clientSideSearchWorkOrders;
        this.matchesSearch = search.matchesSearch;
        this.getRecentWorkOrdersByVin = search.getRecentWorkOrdersByVin;
        // Lifecycle
        this.reopenWorkOrder = lifecycle.reopenWorkOrder;
        this.closeWorkOrder = lifecycle.closeWorkOrder;
        this.getDeleteImpact = lifecycle.getDeleteImpact;
        this.deleteWorkOrder = lifecycle.deleteWorkOrder;
        this.undeleteWorkOrder = lifecycle.undeleteWorkOrder;
        this.approveWorkOrder = lifecycle.approveWorkOrder;
        this.requestReapproval = lifecycle.requestReapproval;
        this.getAvailableDepartments = lifecycle.getAvailableDepartments;
        this.routeToDepartment = lifecycle.routeToDepartment;
        // Credits
        this.getCreditContext = credits.getCreditContext;
        this.createCredit = credits.createCredit;
        this.listCredits = credits.listCredits;
        // Pricing adjustments (discounts & surcharges)
        this.getAdjustments = adjustments.getAdjustments;
        this.addAdjustment = adjustments.addAdjustment;
        this.removeAdjustment = adjustments.removeAdjustment;
        this.previewAdjustments = adjustments.previewAdjustments;
        // Service line operations
        this.assignUserToService = services.assignUserToService;
        this.unassignUserFromService = services.unassignUserFromService;
        this.assignUserToDepartmentServices = services.assignUserToDepartmentServices;
        this.getServiceSplits = services.getServiceSplits;
        this.putServiceSplits = services.putServiceSplits;
        this.deleteServiceSplits = services.deleteServiceSplits;
        this.addServiceToWorkOrder = services.addServiceToWorkOrder;
        this.removeServiceFromWorkOrder = services.removeServiceFromWorkOrder;
        this.updateServiceStatus = services.updateServiceStatus;
        this.updateServicePricing = services.updateServicePricing;
        this.approveService = services.approveService;
        this.denyService = services.denyService;
        this.recordDealershipDecision = services.recordDealershipDecision;
        this.bulkApproveServices = services.bulkApproveServices;
        this.approveWorkOrderFromQueue = services.approveWorkOrderFromQueue;
        this.declineAllServices = services.declineAllServices;
        this.getServiceChildren = services.getServiceChildren;
        this.getServiceRelationships = services.getServiceRelationships;
        this.getServiceHierarchy = services.getServiceHierarchy;
        this.getWorkOrderServices = services.getWorkOrderServices;
        this.getWorkOrderServicesDetailed = services.getWorkOrderServicesDetailed;
        // Notes
        this.listNotes = notes.listNotes;
        this.createNote = notes.createNote;
        this.updateNote = notes.updateNote;
        this.deleteNote = notes.deleteNote;
        this.completeNote = notes.completeNote;
        this.uncompleteNote = notes.uncompleteNote;
        // Tags
        this.listLocationTags = tags.listLocationTags;
        this.createLocationTag = tags.createLocationTag;
        this.updateLocationTag = tags.updateLocationTag;
        this.deleteLocationTag = tags.deleteLocationTag;
        this.getWorkOrderTags = tags.getWorkOrderTags;
        this.addTagsToWorkOrder = tags.addTagsToWorkOrder;
        this.setWorkOrderTags = tags.setWorkOrderTags;
        this.removeTagsFromWorkOrder = tags.removeTagsFromWorkOrder;
        // Files
        this.listWorkOrderFiles = files.listWorkOrderFiles;
        this.getFileRenderUrl = files.getFileRenderUrl;
        this.addFile = files.addFile;
        this.uploadFileOnly = files.uploadFileOnly;
        this.attachFile = files.attachFile;
        this.deleteFile = files.deleteFile;
        this.listServiceFiles = files.listServiceFiles;
        this.deleteServiceFile = files.deleteServiceFile;
        // Invoicing / batches / dashboards
        this.sendInvoice = invoicing.sendInvoice;
        this.getClosedWorkOrdersForDashboard = invoicing.getClosedWorkOrdersForDashboard;
        this.sendDashboardInvoices = invoicing.sendDashboardInvoices;
        this.sendInvoices = invoicing.sendInvoices;
        this.listInvoiceBatches = invoicing.listInvoiceBatches;
        this.searchInvoiceBatches = invoicing.searchInvoiceBatches;
        this.getInvoiceBatch = invoicing.getInvoiceBatch;
        this.listActivitiesByBatch = invoicing.listActivitiesByBatch;
        this.resendDelivery = invoicing.resendDelivery;
        this.resendInvoiceBatch = invoicing.resendInvoiceBatch;
        this.getAwaitingApprovalWorkOrders = invoicing.getAwaitingApprovalWorkOrders;
        this.getApprovalUsers = invoicing.getApprovalUsers;
        this.sendApprovalRequests = invoicing.sendApprovalRequests;
        this.setWorkOrderInvoiceDispositionBulk = invoicing.setWorkOrderInvoiceDispositionBulk;
        this.createInvoiceBatchFromWorkOrders = invoicing.createInvoiceBatchFromWorkOrders;
        this.bulkResendInvoiceBatches = invoicing.bulkResendInvoiceBatches;
        this.getInvoiceSentTo = invoicing.getInvoiceSentTo;
        this.client = client;
        this.baseUrl = baseUrl;
        this.options = options;
    }
    static getInstance(client, baseUrl, options) {
        if (!WorkOrdersService.instances.has(client)) {
            WorkOrdersService.instances.set(client, new WorkOrdersService(client, baseUrl, options));
        }
        return WorkOrdersService.instances.get(client);
    }
    _dedup(key, factory) {
        const existing = this._requestCache.get(key);
        if (existing)
            return existing;
        const promise = factory();
        this._requestCache.set(key, promise);
        promise.finally(() => {
            setTimeout(() => this._requestCache.delete(key), 100);
        });
        return promise;
    }
    /**
     * Failure handling for list-style getters: throw (web) or soft-fail
     * to [] (mobile), per the throwOnListErrors option.
     */
    listFailure(message, error) {
        if (this.options.throwOnListErrors) {
            if (error instanceof Error)
                throw error;
            throw new Error(message);
        }
        return [];
    }
    /**
     * Update the base URL for this service instance
     * Used when switching environments in mobile app
     */
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
    }
    /**
     * Preflight-evaluate the configurable guard rules (work-order hooks) for a
     * mutation that hasn't happened yet — e.g. a draft work order being built.
     * Always resolves 200 with blocks/warnings/overridability; throws only on
     * transport/HTTP failure (advisory callers must catch and fail silent).
     */
    async evaluateGuard(locationId, req) {
        return evaluateGuard(this.client, locationId, req);
    }
}
WorkOrdersService.instances = new Map();
/**
 * Create work orders service instance (Singleton per API client)
 */
export function createWorkOrdersService(apiClient, baseUrl, options) {
    return WorkOrdersService.getInstance(apiClient, baseUrl, options);
}
